import { UserCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Profile } from '@/types/profile';

import { POPUP_LEVEL } from '@/components/ui/popup-level';
import { api, apiUrl } from '@/lib/api';
import { clearToken } from '@/lib/auth';

/** Avatar button + account menu (My account / Settings / Log out), shared across every top bar. */
/**
 * `onGuard` wraps every exit this menu offers, so an editor can raise its unsaved-changes modal
 * first. A THUNK rather than a URL because logout is not just a destination — it clears the
 * token on the way out, and that has to happen if and only if the teacher actually leaves.
 */
export default function AccountMenu({ compact = false, onGuard }: {
  compact?: boolean;
  onGuard?: (run: () => void) => void;
}) {
  const navigate = useNavigate();
  const leave = (run: () => void) => (onGuard ? onGuard(run) : run());
  const { t } = useTranslation();
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get<Profile>('/profile')
      .then((p) => setAvatarSrc(p.avatarUrl ? apiUrl(p.avatarUrl) : `${import.meta.env.BASE_URL}images/avatars/${p.avatar}.png`))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  function toggle() {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    setOpen((o) => !o);
  }

  const size = compact ? 'h-8 w-8' : 'h-9 w-9';
  const item = 'flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm font-medium text-ws-text hover:bg-ws-hover';

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        aria-label={t('common.myAccount')}
        aria-haspopup='menu'
        aria-expanded={open}
        className={`flex ${size} items-center justify-center overflow-hidden rounded-full text-ws-muted hover:opacity-80 ${open ? 'ring-2 ring-ws-primary' : 'border border-ws-border'}`}
      >
        {avatarSrc ? <img src={avatarSrc} alt='' className='h-full w-full object-cover' /> : <UserCircle className='h-5 w-5' />}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role='menu'
          style={{ top: pos.top, right: pos.right }}
          className={`fixed ${POPUP_LEVEL} w-48 overflow-hidden rounded-md border border-ws-border bg-ws-elevated shadow-lg`}
        >
          <button role='menuitem' onClick={() => { leave(() => navigate('/app/account')); setOpen(false); }} className={item}>{t('common.myAccount')}</button>
          <div className='border-t border-ws-border' />
          <button role='menuitem' onClick={() => { leave(() => navigate('/app/settings')); setOpen(false); }} className={item}>{t('common.settings')}</button>
          <div className='border-t border-ws-border' />
          {/* Logout clears the token BEFORE navigating, which is why the guard takes a thunk
              rather than a URL: deferring a bare '/login' would send a still-authenticated user
              to a public-only route and bounce them straight back. */}
          <button role='menuitem' onClick={() => { leave(() => { clearToken(); navigate('/login'); }); setOpen(false); }} className={item}>{t('common.logout')}</button>
        </div>,
        document.body,
      )}
    </>
  );
}
